// En üstte ekleyin
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Autocomplete,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import { Row, Col } from 'antd';
import React, { useState } from 'react';

interface Product {
  itemCode: string;
  itemName: string;
  unitPrice: number;
  unit: string;
}

// OrderLineForm arayüzüne KDV ve indirim alanları ekleyelim
interface OrderLineForm {
  id: string;
  lineNumber: number;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  taxRate: number; // KDV oranı
  discountRate: number; // İndirim oranı
  netAmount: number; // Net tutar (indirim sonrası)
  taxAmount: number; // KDV tutarı
  totalPrice: number; // Toplam tutar
  unit: string;
  description: string;
}

interface OrderLinesProps {
  lines: OrderLineForm[];
  products: Product[];
  onAddLine: () => void;
  onUpdateLine: (lineId: string, field: keyof OrderLineForm, value: any) => void;
  onDeleteLine: (lineId: string) => void;
  onProductSelect: (product: Product) => void;
}

const OrderLines: React.FC<OrderLinesProps> = ({
  lines,
  products,
  onAddLine,
  onUpdateLine,
  onDeleteLine,
  onProductSelect
}) => {
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const handleProductDialogOpen = () => {
    setProductDialogOpen(true);
  };

  const handleProductDialogClose = () => {
    setProductDialogOpen(false);
    setSelectedProduct(null);
  };

  const handleProductAdd = () => {
    if (selectedProduct) {
      onProductSelect(selectedProduct);
      handleProductDialogClose();
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Sipariş Kalemleri
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={handleProductDialogOpen}
            sx={{ mr: 1 }}
          >
            Ürün Seç
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddLine}
          >
            Yeni Satır
          </Button>
        </Box>
      </Box>

      {lines.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Henüz sipariş kalemi eklenmedi
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddLine}
            sx={{ mt: 2 }}
          >
            İlk Kalemi Ekle
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            // TableHead içine KDV ve indirim sütunları ekleyelim
            <TableHead>
              <TableRow>
                <TableCell>Sıra</TableCell>
                <TableCell>Ürün Kodu</TableCell>
                <TableCell>Ürün Adı</TableCell>
                <TableCell>Miktar</TableCell>
                <TableCell>Birim</TableCell>
                <TableCell>Birim Fiyat</TableCell>
                <TableCell>KDV %</TableCell>
                <TableCell>İndirim %</TableCell>
                <TableCell>Net Tutar</TableCell>
                <TableCell>KDV Tutarı</TableCell>
                <TableCell>Toplam</TableCell>
                <TableCell>Açıklama</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            // TableBody içindeki satırlara KDV ve indirim alanları ekleyelim
            <TableBody>
              {lines.map((line) => (
                <TableRow key={line.id}>
                  <TableCell>{line.lineNumber}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={line.itemCode}
                      onChange={(e) => onUpdateLine(line.id, 'itemCode', e.target.value)}
                      placeholder="Ürün kodu"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={line.itemName}
                      onChange={(e) => onUpdateLine(line.id, 'itemName', e.target.value)}
                      placeholder="Ürün adı"
                      sx={{ minWidth: 200 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={line.quantity}
                      onChange={(e) => onUpdateLine(line.id, 'quantity', parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={line.unit}
                      onChange={(e) => onUpdateLine(line.id, 'unit', e.target.value)}
                      placeholder="Birim"
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={line.unitPrice}
                      onChange={(e) => onUpdateLine(line.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={line.taxRate || 18}
                      onChange={(e) => onUpdateLine(line.id, 'taxRate', parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={line.discountRate || 0}
                      onChange={(e) => onUpdateLine(line.id, 'discountRate', parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(line.netAmount || line.quantity * line.unitPrice * (1 - (line.discountRate || 0) / 100))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(line.taxAmount || (line.quantity * line.unitPrice * (1 - (line.discountRate || 0) / 100)) * ((line.taxRate || 18) / 100))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(line.totalPrice)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={line.description}
                      onChange={(e) => onUpdateLine(line.id, 'description', e.target.value)}
                      placeholder="Açıklama"
                      sx={{ minWidth: 150 }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="error"
                      onClick={() => onDeleteLine(line.id)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Ürün Seçim Dialog */}
      <Dialog
        open={productDialogOpen}
        onClose={handleProductDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Ürün Seç</DialogTitle>
        <DialogContent>
          <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
            <Col xs={24}>
              <Autocomplete
                options={products}
                getOptionLabel={(option) => `${option.itemCode} - ${option.itemName}`}
                value={selectedProduct}
                onChange={(_, value) => setSelectedProduct(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ürün"
                    placeholder="Ürün arayın..."
                    variant="outlined"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.itemCode === value.itemCode}
              />
            </Col>
            {selectedProduct && (
              <Col xs={24}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2">Seçilen Ürün:</Typography>
                  <Typography variant="body2">
                    <strong>Kod:</strong> {selectedProduct.itemCode}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Ad:</strong> {selectedProduct.itemName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Birim Fiyat:</strong> {formatCurrency(selectedProduct.unitPrice)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Birim:</strong> {selectedProduct.unit}
                  </Typography>
                </Paper>
              </Col>
            )}
          </Row>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProductDialogClose}>İptal</Button>
          <Button
            onClick={handleProductAdd}
            variant="contained"
            disabled={!selectedProduct}
          >
            Ekle
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderLines;